"use client";
import "../../styles.web.css";

import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import Image from "@tiptap/extension-image";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered,
  Strikethrough,
  Code,
  Quote,
  Minus,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Image as ImageIcon,
  Palette,
  Highlighter,
} from "lucide-react";
import CustomButton from "../common/general/CustomButton/CustomButton";
import CustomText from "../common/general/CustomText/CustomText";
import { View, Pressable, GestureResponderEvent } from "react-native";
import { Menu } from "react-native-paper";
import { CaretDownIcon, LineVerticalIcon } from "@phosphor-icons/react";
import Link from "@tiptap/extension-link";
import { Link as LinkIcon } from "lucide-react";
import useFileUpload from "../../hooks/useFileUpload";
import { StoragePathType, useGetGcpSignedUrlForReadQuery } from "../../graphql/generated/graphql";

const ImageWithData = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() as Record<string, any>),
      "data-file-id": {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-file-id"),
        renderHTML: (attributes: Record<string, any>) => {
          const value = attributes["data-file-id"];
          return value ? { "data-file-id": value } : {};
        },
      },
    };
  },
});

type TextStyle = "paragraph" | "h1" | "h2" | "h3";
const textStyleOptions: { value: TextStyle; label: string }[] = [
  { value: "paragraph", label: "Normal text" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
];

type NamedColor = { name: string; value: string };
const TEXT_COLORS: NamedColor[] = [
  { name: "Black", value: "#111827" },
  { name: "Dark Gray", value: "#374151" },
  { name: "Gray", value: "#6B7280" },
  { name: "Red", value: "#EF4444" },
  { name: "Green", value: "#10B981" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Purple", value: "#A855F7" },
  { name: "Orange", value: "#F59E0B" },
];
const HIGHLIGHT_COLORS: NamedColor[] = [
  { name: "Light Yellow", value: "#FEF3C7" },
  { name: "Yellow", value: "#FDE68A" },
  { name: "Light Green", value: "#D1FAE5" },
  { name: "Light Blue", value: "#DBEAFE" },
  { name: "Lavender", value: "#E9D5FF" },
  { name: "Light Red", value: "#FECACA" },
  { name: "Light Gray", value: "#F5F5F5" },
];

export interface TextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  makeImagesPublic?: boolean;
  restrictImageUpload?: boolean;
}

const TextEditor: React.FC<TextEditorProps> = ({
  value = "",
  onChange,
  disabled = false,
  placeholder = "Start typing…",
  style,
  makeImagesPublic = false,
  restrictImageUpload = false,
}) => {
  const [blockType, setBlockType] = useState<TextStyle>("paragraph");
  const textStyleDropdownRef = useRef<View>(null);
  const [textStyleMenuVisible, setTextStyleMenuVisible] = useState(false);
  const [textStyleMenuPosition, setTextStyleMenuPosition] = useState({ x: 0, y: 0 });

  const textColorButtonRef = useRef<View>(null);
  const [textColorMenuVisible, setTextColorMenuVisible] = useState(false);
  const [textColorMenuPosition, setTextColorMenuPosition] = useState({ x: 0, y: 0 });

  const highlightButtonRef = useRef<View>(null);
  const [highlightMenuVisible, setHighlightMenuVisible] = useState(false);
  const [highlightMenuPosition, setHighlightMenuPosition] = useState({ x: 0, y: 0 });

  const lastSelectionRef = React.useRef<{ from: number; to: number } | null>(null);

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.RichTextEditorImages,
    onError: (message) => console.error("Image upload failed:", message),
    isPublic: makeImagesPublic,
  });

  const { refetch: refetchGetGcpSignedUrlForRead } = useGetGcpSignedUrlForReadQuery({
    variables: {
      fileId: "",
      responseDisposition: "inline",
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Underline,
      Highlight.configure({ multicolor: true }),
      ImageWithData,
      Subscript,
      Superscript,
      Typography,
      HorizontalRule,

      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: true,
        autolink: false,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank", class: "axio-link" },
        validate: (href) => /^(https?:\/\/|mailto:|tel:)/i.test(href),
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onSelectionUpdate: ({ editor }) => {
      const { from, to, empty } = editor.state.selection;
      if (!empty) lastSelectionRef.current = { from, to };
      if (editor.isActive("heading", { level: 1 })) return setBlockType("h1");
      if (editor.isActive("heading", { level: 2 })) return setBlockType("h2");
      if (editor.isActive("heading", { level: 3 })) return setBlockType("h3");
      return setBlockType("paragraph");
    },
  });

  const handleInsertLink = () => {
    let url = window.prompt("Enter the URL", "https://");
    if (!url) return;

    if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) url = `https://${url}`;

    const { empty } = editor.state.selection;

    if (!empty) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        text: url,
        marks: [{ type: "link", attrs: { href: url } }],
      })
      .insertContent(" ")
      .run();
  };

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      const { from, to } = editor.state.selection;
      editor.commands.setContent(value, { emitUpdate: false });
      editor.commands.setTextSelection({ from, to });
    }
  }, [value, editor]);

  if (!editor) return null;

  const applyBlockType = (value: TextStyle) => {
    const chain = editor.chain().focus();
    if (value === "paragraph") {
      chain.setParagraph().run();
      setBlockType("paragraph");
      return;
    }
    const level = (value === "h1" ? 1 : value === "h2" ? 2 : 3) as 1 | 2 | 3;
    chain.setHeading({ level }).run();
    setBlockType(value);
  };

  const handleMenuOpen = (event: GestureResponderEvent) => {
    if (textStyleDropdownRef.current) {
      textStyleDropdownRef.current.measure((fx, fy, width, height, px, py) => {
        setTextStyleMenuPosition({ x: px, y: py + height });
        setTextStyleMenuVisible(true);
      });
    }
  };

  const handleTextColorMenuOpen = () => {
    if (textColorButtonRef.current) {
      textColorButtonRef.current.measure((fx, fy, width, height, px, py) => {
        setTextColorMenuPosition({ x: px, y: py + height });
        setTextColorMenuVisible(true);
      });
    }
  };

  const handleHighlightMenuOpen = () => {
    if (highlightButtonRef.current) {
      highlightButtonRef.current.measure((fx, fy, width, height, px, py) => {
        setHighlightMenuPosition({ x: px, y: py + height });
        setHighlightMenuVisible(true);
      });
    }
  };

  const handleImageUpload = async () => {
    const result = await handleFileUpload();
    if (result && result.length > 0) {
      const data = await refetchGetGcpSignedUrlForRead({
        fileId: result[0].file.id,
        responseDisposition: "inline",
      });
      if (data?.data?.getGcpSignedUrlForRead?.data?.length > 0) {
        const signedUrl = data.data.getGcpSignedUrlForRead.data[0].signedUrl;

        const content = `<img data-file-id="axio-fileid://${result[0].file.id}" src="${signedUrl}" />`;
        editor.chain().focus().insertContent(content).run();
      }
    }
  };

  const currentLabel = textStyleOptions.find((o) => o.value === blockType)?.label ?? "Normal text";

  return (
    <div className="border rounded-lg p-2 space-y-2" style={style}>
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Pressable
          ref={textStyleDropdownRef}
          onPress={handleMenuOpen}
          disabled={false}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 6,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            backgroundColor: "#ffffff",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}>
          <CustomText text={currentLabel} size="s" useTranslationText={false} />
          <CaretDownIcon size={16} />
        </Pressable>

        <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />

        <CustomButton
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
          onPress={handleInsertLink}
          styleType="lightTransparent"
          size="s"
          leftIcon={<LinkIcon width={16} height={16} />}
        />

        <CustomButton
          onPress={() => editor.chain().focus().toggleBold().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Bold width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleItalic().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Italic width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleUnderline().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<UnderlineIcon width={16} height={16} />}
          text=""
        />

        <CustomButton
          onPress={() => editor.chain().focus().toggleStrike().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Strikethrough width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleCode().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Code width={16} height={16} />}
          text=""
        />

        <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />

        <CustomButton
          onPress={() => editor.chain().focus().toggleBulletList().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<ListIcon width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleOrderedList().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<ListOrdered width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleBlockquote().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Quote width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleCodeBlock().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Code width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().setHorizontalRule().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<Minus width={16} height={16} />}
          text=""
        />

        <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />

        <CustomButton
          onPress={() => editor.chain().focus().toggleSubscript().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<SubscriptIcon width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().toggleSuperscript().run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<SuperscriptIcon width={16} height={16} />}
          text=""
        />

        <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />

        <CustomButton
          onPress={() => editor.chain().focus().setTextAlign("left").run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<AlignLeft width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().setTextAlign("center").run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<AlignCenter width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().setTextAlign("right").run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<AlignRight width={16} height={16} />}
          text=""
        />
        <CustomButton
          onPress={() => editor.chain().focus().setTextAlign("justify").run()}
          styleType="lightTransparent"
          size="s"
          leftIcon={<AlignJustify width={16} height={16} />}
          text=""
        />

        <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />

        {!restrictImageUpload && (
          <CustomButton
            onPress={handleImageUpload}
            styleType="lightTransparent"
            size="s"
            leftIcon={<ImageIcon width={16} height={16} />}
            text=""
          />
        )}

        {!restrictImageUpload && <LineVerticalIcon size={32} weight="light" color="#e5e7eb" />}

        <Pressable
          ref={textColorButtonRef}
          onPress={handleTextColorMenuOpen}
          disabled={false}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 6,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            backgroundColor: "#ffffff",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}>
          <Palette width={16} height={16} />
        </Pressable>

        <Pressable
          ref={highlightButtonRef}
          onPress={handleHighlightMenuOpen}
          disabled={false}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 6,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "#e5e7eb",
            backgroundColor: "#ffffff",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}>
          <Highlighter width={16} height={16} />
        </Pressable>
      </View>

      <Menu
        visible={textStyleMenuVisible}
        onDismiss={() => setTextStyleMenuVisible(false)}
        anchor={{ x: textStyleMenuPosition.x, y: textStyleMenuPosition.y }}
        contentStyle={{
          backgroundColor: "#fff",
          marginTop: 4,
          width: 100,
          display: "flex",
          zIndex: 9999,
        }}>
        {textStyleOptions.map((opt) => (
          <Menu.Item
            key={opt.value}
            onPress={() => {
              applyBlockType(opt.value);
              setTextStyleMenuVisible(false);
            }}
            title={opt.label}
            titleStyle={{
              fontSize: 14,
              color: "#374151",
            }}
          />
        ))}
      </Menu>

      <Menu
        visible={textColorMenuVisible}
        onDismiss={() => setTextColorMenuVisible(false)}
        anchor={{ x: textColorMenuPosition.x, y: textColorMenuPosition.y }}
        contentStyle={{
          backgroundColor: "#fff",
          marginTop: 4,
          width: 140,
          display: "flex",
          zIndex: 9999,
        }}>
        {TEXT_COLORS.map((item) => (
          <Menu.Item
            key={item.value}
            onPress={() => {
              editor.chain().focus().setColor(item.value).run();
              setTextColorMenuVisible(false);
            }}
            title={item.name}
            titleStyle={{ fontSize: 14, color: "#374151" }}
            leadingIcon={() => (
              <View
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: item.value,
                  marginRight: 8,
                }}
              />
            )}
          />
        ))}
        <Menu.Item
          onPress={() => {
            editor.chain().focus().unsetColor().run();
            setTextColorMenuVisible(false);
          }}
          title="Clear text color"
          titleStyle={{ fontSize: 14, color: "#374151" }}
        />
      </Menu>

      <Menu
        visible={highlightMenuVisible}
        onDismiss={() => setHighlightMenuVisible(false)}
        anchor={{ x: highlightMenuPosition.x, y: highlightMenuPosition.y }}
        contentStyle={{
          backgroundColor: "#fff",
          marginTop: 4,
          width: 140,
          display: "flex",
          zIndex: 9999,
        }}>
        {HIGHLIGHT_COLORS.map((item) => (
          <Menu.Item
            key={item.value}
            onPress={() => {
              editor.chain().focus().setHighlight({ color: item.value }).run();
              setHighlightMenuVisible(false);
            }}
            title={item.name}
            titleStyle={{ fontSize: 14, color: "#374151" }}
            leadingIcon={() => (
              <View
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: item.value,
                  marginRight: 8,
                  borderWidth: 1,
                  borderColor: "#e5e7eb",
                }}
              />
            )}
          />
        ))}
        <Menu.Item
          onPress={() => {
            editor.chain().focus().unsetHighlight().run();
            setHighlightMenuVisible(false);
          }}
          title="Clear highlight"
          titleStyle={{ fontSize: 14, color: "#374151" }}
        />
      </Menu>

      <EditorContent editor={editor} className="axio-editor" />
    </div>
  );
};

export default TextEditor;
